import { useEffect, useState } from 'react';
import { fetchQuotes } from '../lib/api/quotes';
import type { AlarmDirection, ApiQuote, ChartSymbol } from '../types/api';
import type { AlarmTimeframe } from './alarmTimeframes';

interface UseAlarmSetupInput {
  symbol: ChartSymbol | null;
  initialPrice?: number;
}

export function useAlarmSetup({ symbol, initialPrice }: UseAlarmSetupInput) {
  const [timeframe, setTimeframe] = useState<AlarmTimeframe>('1Y');
  const [targetPrice, setTargetPrice] = useState<number | null>(initialPrice ?? null);
  const [latestQuote, setLatestQuote] = useState<ApiQuote | null>(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setTargetPrice(initialPrice ?? null);
  }, [initialPrice, symbol?.name]);

  useEffect(() => {
    if (!symbol) {
      setLatestQuote(null);
      return;
    }

    let cancelled = false;
    setLoadingQuote(true);
    setError('');
    void fetchQuotes([symbol.name]).then((quotes) => {
      if (cancelled) return;
      const quote = quotes[0] ?? null;
      setLatestQuote(quote);
      if (quote) setTargetPrice((current) => current ?? quote.price);
    }).catch((err) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : 'Failed to load latest quote');
    }).finally(() => {
      if (!cancelled) setLoadingQuote(false);
    });

    return () => {
      cancelled = true;
    };
  }, [symbol?.name]);

  const latestPrice = latestQuote?.price ?? initialPrice ?? null;
  const direction: AlarmDirection = latestPrice !== null && targetPrice !== null && targetPrice < latestPrice ? 'below' : 'above';
  const valid = targetPrice !== null && Number.isFinite(targetPrice) && targetPrice > 0;

  return {
    timeframe,
    setTimeframe,
    targetPrice,
    setTargetPrice,
    latestQuote,
    latestPrice,
    direction,
    loadingQuote,
    error,
    valid,
  };
}
